import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { JurisdictionService } from '../../../domain/services/jurisdiction/jurisdiction.service';
import { Jurisdiction } from '../../../domain/models/distribution.model';

/**
 * Jurisdictions admin — collection-commission rate and active flag per jurisdiction.
 * V14 only seeded jurisdictions by backfilling distinct free-text property strings, so a
 * county that never recorded them starts empty with nothing to distribute to (and an empty
 * municipality select during registration). Hence create is supported here.
 * Previously lived inline on the Distribution runs page; moved here under Administrative.
 */
@Component({
  selector: 'app-jurisdictions',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCardModule, MatButtonModule, MatIconModule, MatProgressBarModule],
  templateUrl: './jurisdictions.component.html',
  styleUrl: './jurisdictions.component.css',
})
export class JurisdictionsComponent implements OnInit {
  jurisdictions: Jurisdiction[] = [];
  loading = false;
  busy = false;
  error: string | null = null;
  savedId: number | null = null;

  newName = '';
  newRate: number | null = null;

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly jurisdictionService: JurisdictionService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/admin/jurisdictions', label: 'Administrative' },
      { icon: 'account_balance', url: '/app/admin/jurisdictions', label: 'Jurisdictions' },
    ]);
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.jurisdictionService.listAll().subscribe({
      next: (list) => { this.jurisdictions = list; this.loading = false; },
      error: () => { this.error = 'Failed to load jurisdictions.'; this.loading = false; },
    });
  }

  add(): void {
    const name = this.newName.trim();
    if (!name) return;
    this.busy = true;
    this.error = null;
    this.jurisdictionService.create(name, this.newRate ?? 0).subscribe({
      next: () => {
        this.busy = false;
        this.newName = '';
        this.newRate = null;
        this.load();
      },
      error: (e) => {
        this.busy = false;
        this.error = e?.error?.message || 'Failed to add jurisdiction.';
      },
    });
  }

  save(j: Jurisdiction): void {
    this.busy = true;
    this.error = null;
    this.jurisdictionService.update(j.id, j.commissionRate, j.active).subscribe({
      next: () => {
        this.busy = false;
        this.savedId = j.id;
        setTimeout(() => { if (this.savedId === j.id) { this.savedId = null; } }, 2000);
      },
      error: () => { this.busy = false; this.error = 'Failed to save jurisdiction.'; },
    });
  }
}
